<!--
    ========================================
    Unit Types Table Card
    ========================================
-->
<app-card>

    <ng-template appTemplate="bodyTemplate">

        <app-table #tableApi="tableApi" [columns]="tableColumns" [value]="unitTypesList()" (onInit)="onTableInitialized()">

            <!-- start captionTemplate -->
            <ng-template appTemplate="captionTemplate">

                <h5 class="mb-4">{{ TRANSLATE_KEY_MAP.TITLE() | translate }}</h5>

                <div class="d-flex justify-content-between align-items-center gap-3">

                    <app-table-search />

                    <button appButton appWaves variant="primary" (click)="showCreateForm()">
                        <i class="ti ti-plus me-1 icon-base"></i>
                        {{ TRANSLATE_KEY_MAP.FORM.ADD_UNIT_TYPE() | translate }}
                    </button>

                </div>

            </ng-template>

            <!-- start theadTemplate -->
            <ng-template appTemplate="theadTemplate" let-visibleColumns>
                <tr class="cell-fit">
                    <th scope="col">
                        <app-table-header-checkbox name="table_select_rows_all" id="table_select_rows_all" />
                    </th>
                    @for (column of visibleColumns; track column.id) {
                        <th
                            scope="col"
                            [style.width]="column.width"
                            [style.text-align]="column.align"
                            [skipSortable]="!column.sortable"
                            [appTableSortableColumn]="column.field"
                        >
                            <div class="d-flex align-items-center gap-2" [class]="column.headerClassStyle">
                                <span>{{ column.header | translate }}</span>
                                @if (column.sortable && column.field; as field) {
                                    <app-table-sort-icon [field]="field" />
                                }
                            </div>
                        </th>
                    }
                </tr>
            </ng-template>

            <!-- start tbodyTemplate -->
            <ng-template appTemplate="tbodyTemplate" let-row let-menuActions="menuActions">
                <tr>

                    <td style="width: 50px;">
                        <app-table-checkbox [value]="row" [name]="row.id" [id]="row.id" />
                    </td>

                    <!-- Name -->
                    <td>{{ row.name }}</td>

                    <!-- Description -->
                    <td>{{ row.description | truncate: 50 }}</td>

                    <!-- Level -->
                    <td>{{ row.level }}</td>

                    <!-- Default type for its level badge -->
                    <td>
                        @let defaultTypeLevelBadge = row.defaultTypeLevelBadge();
                        <span class="w-100" [appBadge]="defaultTypeLevelBadge.variant" [appearance]="defaultTypeLevelBadge.appearance">
                            {{ defaultTypeLevelBadge.label | translate }}
                        </span>
                    </td>

                    <!-- Icon -->
                    <td>
                        @if (row.icon) {
                            <span>{{ row.icon }}</span>
                        } @else {
                            <span appBadge="secondary" appearance="label">{{ 'GENERAL.NA' | translate }}</span>
                        }
                    </td>

                    <!-- Created On -->
                    <td>
                        <span>{{ row.createdOn | date:'dd-MM-yyyy' }}</span>
                    </td>

                    <!-- Actions -->
                    <td class="text-center">
                        <app-dropdown
                            buttonSize="sm"
                            [iconOnly]="true"
                            [hideArrow]="true"
                            buttonVariant="primary"
                            buttonAppearance="text"
                            icon="fa-solid fa-ellipsis-vertical"
                        >
                            <ng-template appTemplate="dropdownMenuTemplate">
                                <ul class="dropdown-menu dropdown-menu-end">
                                    @for (action of getRowActions(row); track action.label) {
                                        @if (action.addDivider){
                                            <li>
                                                <hr class="dropdown-divider">
                                            </li>
                                        }
                                        <li>
                                            <button 
                                                appWaves
                                                type="button"
                                                class="dropdown-item"
                                                [class]="action?.classStyle"
                                                (click)="action?.command(row)">
                                                <i class="me-1 icon-base" [class]="action?.icon"></i>
                                                <span class="align-middle">
                                                    {{ action.label | translate }}
                                                </span>
                                            </button>
                                        </li>
                                    }
                                </ul>
                            </ng-template>
                        </app-dropdown>
                    </td>

                </tr>
            </ng-template>

        </app-table>

    </ng-template>

</app-card>


<!--
    ========================================
    Form Modal (Add / Edit)
    ========================================
-->
@let isFormLoading = formRef()?.isLoading() ?? false;
@let isFormCreationMode = formRef()?.isCreationMode() ?? true;
@let formTitle = (isFormCreationMode ? TRANSLATE_KEY_MAP.FORM.ADD_UNIT_TYPE() : TRANSLATE_KEY_MAP.FORM.EDIT_UNIT_TYPE()) | translate;

<app-modal
    [centered]="true"
    [title]="formTitle"
    backdrop="static"
    [cornerButton]="false"
    [(visible)]="formModal"
>

    <!--
        Form component is a direct view child of this component (NOT inside ng-template),
        so that viewChild can resolve it. It is projected into the modal body via ng-content.
    -->
    <app-unit-type-form modalBody (submitted)="onFormSubmitted()" />

    <div modalFooter class="d-flex justify-content-end flex-wrap flex-column-reverse flex-sm-row gap-3 w-100">
        <button
            appButton
            appWaves
            variant="secondary"
            appearance="label"
            class="flex-grow-1 flex-sm-grow-0"
            [disabled]="isFormLoading"
            (click)="toggleFormModal()"
        >
            {{ 'GENERAL.CANCEL' | translate }}
        </button>
        <button
            appButton
            appWaves
            variant="primary"
            class="flex-grow-1"
            [disabled]="(formRef()?.operationsForm?.invalid ?? true) || isFormLoading"
            (click)="formRef()?.submit()"
        >
            @if (isFormLoading) {
                <app-spin-kit-wave style="--sk-size: 20px;" bgColor="var(--bs-white)" />
            } @else {
                {{ (isFormCreationMode ? 'GENERAL.CREATE' : 'GENERAL.SAVE') | translate }}
            }
        </button>
    </div>

</app-modal>


<!--
    ========================================
    Delete Modal
    ========================================
-->
<app-modal
    [(visible)]="deleteModal"
    [centered]="true"
    [cornerButton]="false"
    [backdrop]="loading() ? 'static' : true"
    [title]="TRANSLATE_KEY_MAP.FORM.DELETE_CONFIRM() | translate"
>

    <ng-template appTemplate="bodyTemplate">
        @if (selectedUnitType(); as unitType) {
            <div appListGroup>
                <div appListGroupItem class="d-flex justify-content-between align-items-start">
                <div class="d-flex align-items-start flex-column">
                    <h6 class="mb-1">{{unitType.name}}</h6>
                    <small>{{unitType.description}}</small>
                </div>
                @let defaultTypeLevelBadge = unitType.defaultTypeLevelBadge();
            
                @if(defaultTypeLevelBadge != null) {
                    <span [appBadge]="defaultTypeLevelBadge.variant" [appearance]="defaultTypeLevelBadge.appearance">
                        {{ defaultTypeLevelBadge.label | translate }}
                    </span>
                }
                </div>
            </div>
        }
    </ng-template>

    <ng-template appTemplate="footerTemplate">
        <div class="d-flex justify-content-end flex-wrap flex-column-reverse flex-sm-row gap-3 w-100">
            <button
                appButton
                appWaves
                variant="secondary"
                appearance="label"
                class="flex-grow-1 flex-sm-grow-0"
                [disabled]="loading()"
                (click)="toggleDeleteModal()"
            >
                {{ 'GENERAL.CANCEL' | translate }}
            </button>
            <button
                appButton
                appWaves
                variant="danger"
                class="flex-grow-1"
                [disabled]="loading()"
                (click)="deleteUnitType()"
            >
                @if (loading()) {
                    <app-spin-kit-wave style="--sk-size: 20px;" bgColor="var(--bs-white)" />
                } @else {
                    {{ 'GENERAL.DELETE' | translate }}
                }
            </button>
        </div>
    </ng-template>

</app-modal>
